/** Die Kacheln der Übersicht als einzelne Bausteine fuer den Katalog (D-07). */
import { Link } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { Repeat } from 'lucide-react';

import { db } from '@/db/schema';
import { Kennzahl, KennzahlZeile, StatKarte } from '@/ui/composites/Statistik';
import { euro } from '@/domain/finanzen/finanzen';
import { tagesAuswertung } from '@/domain/habits/daily';
import { dauerText, lernzeitProFach } from '@/domain/school/studyTime';
import { zeitFortschritt } from '@/domain/uebersicht/zeitFortschritt';
import { useFinanzen } from '@/modules/finanzen/useFinanzen';
import { zahl1 } from '@/modules/training/format';
import { useTrainingWoche } from '@/modules/training/useTrainingWoche';

import { Card, Empty } from '@/modules/dashboard/Card';
import { HeuteBlock, UeberfaelligBlock } from '@/modules/dashboard/AufgabenBloecke';
import { KalenderBlock } from '@/modules/dashboard/KalenderBlock';
import { BereicheBlock } from '@/modules/dashboard/BereicheBlock';
import { ZukunftBlock } from '@/modules/dashboard/ZukunftBlock';
import { ZeitBlock } from '@/modules/dashboard/ZeitBlock';
import { DayOverview } from '@/modules/habits/components/DayOverview';
import { useHabitsWithStats } from '@/modules/habits/useHabits';
import { useUnerledigt } from '@/modules/tasks/useTasks';
import { useAreaFilter } from '@/store/areaFilter';
import { fuerHeute, ueberfaellig } from '@/domain/tasks/faellig';
import { toLocalDate, today } from '@/lib/datetime';

export function HeuteKachel() {
  const heute = today();
  return <HeuteBlock aufgaben={fuerHeute(useUnerledigt(), heute)} heute={heute} />;
}

export function UeberfaelligKachel() {
  const heute = today();
  return <UeberfaelligBlock aufgaben={ueberfaellig(useUnerledigt(), heute)} heute={heute} />;
}

export function GewohnheitenHeuteKachel() {
  const heute = today();
  const habits = useHabitsWithStats();
  const { isActive: gefiltert } = useAreaFilter();
  return habits.length > 0 ? (
    <DayOverview eintraege={habits} heute={heute} gefiltert={gefiltert} kompakt />
  ) : (
    <Card title="Gewohnheiten heute" icon={Repeat} to="/gewohnheiten">
      <Empty>Noch keine Gewohnheiten angelegt.</Empty>
    </Card>
  );
}

export const KalenderKachel = () => <KalenderBlock heute={today()} />;
export const ZeitverteilungKachel = () => <ZeitBlock heute={today()} />;
export const BereicheKachel = () => <BereicheBlock />;
export const ZukunftKachel = () => <ZukunftBlock heute={today()} />;

// --- Neu in D-00b: Zeit-Fortschritt, Tageskennzahlen, Ausgaben (B-06) ---------

export function ZeitFortschrittKachel() {
  const balken = zeitFortschritt(today());
  return (
    <StatKarte titel="Zeit-Fortschritt" frage="Wie weit ist die Woche, der Monat, das Jahr?">
      <ul className="flex flex-col gap-2">
        {balken.map((b) => (
          <li key={b.key} className="flex flex-col gap-0.5">
            <div className="flex items-baseline text-[12px]"><span className="flex-1 text-ink-muted">{b.label}</span><span className="tabular-nums">{b.text}</span></div>
            <div className="h-1.5 overflow-hidden rounded-full bg-surface-raised" role="progressbar" aria-label={b.label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(b.anteil * 100)}>
              <div className="h-full rounded-full bg-accent" style={{ width: `${Math.round(b.anteil * 100)}%` }} />
            </div>
          </li>
        ))}
      </ul>
    </StatKarte>
  );
}

export function TageskennzahlenKachel() {
  const heute = today();
  const offen = fuerHeute(useUnerledigt(), heute).length;
  const erledigt = useLiveQuery(async () => (await db.tasks.filter((t) => !!t.completedAt && toLocalDate(new Date(t.completedAt)) === heute).count()), [heute], 0);
  const habits = useHabitsWithStats();
  const tag = tagesAuswertung(habits.filter((e) => !e.habit.archived), heute);
  const woche = useTrainingWoche(heute);
  const minuten = useLiveQuery(async () => {
    const s = await db.studySessions.toArray();
    return [...lernzeitProFach(s, new Date(), heute).values()].reduce((a, b) => a + b, 0);
  }, [heute], 0);
  const habitsGesamt = tag.erledigt.length + tag.offen.length;
  return (
    <StatKarte titel="Heute in Zahlen" frage="Wo stehe ich heute?">
      <KennzahlZeile spalten={2}>
        <Kennzahl label="Aufgaben heute" wert={`${erledigt} / ${erledigt + offen}`} hinweis={offen ? `${offen} noch offen` : 'alles erledigt'} href="/aufgaben" />
        <Kennzahl label="Gewohnheiten" wert={habitsGesamt ? `${tag.erledigt.length} / ${habitsGesamt}` : '–'} hinweis={habitsGesamt ? 'heute abgehakt' : 'heute keine vorgesehen'} href="/gewohnheiten" />
        <Kennzahl label="Training diese Woche" wert={woche ? `${woche.erledigt} / ${woche.geplant}` : '–'} hinweis={woche ? `${zahl1(woche.km)} km gelaufen` : undefined} href="/training" />
        <Kennzahl label="Lernzeit heute" wert={dauerText(minuten)} hinweis="Schule" href="/schule/faecher" />
      </KennzahlZeile>
    </StatKarte>
  );
}

/** B-06: Ausgaben diesen Monat - ausgegeben von Budget, größte Kategorien, Tagesbudget. */
export function AusgabenKachel() {
  const f = useFinanzen();
  const budget = f.stand.reduce((s, z) => s + z.budget, 0);
  const grosse = f.stand.filter((z) => z.ausgegeben > 0).slice(0, 3);
  const max = Math.max(1, ...grosse.map((z) => z.ausgegeben));
  return (
    <StatKarte titel="Ausgaben diesen Monat" frage="Wofür geht mein Geld drauf?" aktion={<Link to="/finanzen" className="text-[12px] text-accent-text hover:underline">Finanzen</Link>}>
      <p className="text-[22px] font-extrabold tabular-nums">{euro(f.summe.ausgaben)}{budget > 0 && <span className="text-sm font-medium text-ink-faint"> von {euro(budget)}</span>}</p>
      {grosse.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {grosse.map((z) => (
            <li key={z.envelope?.id ?? 'ohne'} className="flex items-center gap-2 text-[12px]">
              <span className="w-24 shrink-0 truncate text-ink-muted">{z.name}</span>
              <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-raised"><span className="block h-full rounded-full bg-accent" style={{ width: `${Math.round((z.ausgegeben / max) * 100)}%` }} /></span>
              <span className="w-20 text-right tabular-nums">{euro(z.ausgegeben)}</span>
            </li>
          ))}
        </ul>
      )}
      {f.tagesbudget !== null && <p className="text-[12px] text-ink-muted">Noch {euro(f.tagesbudget)} pro Tag bis Monatsende.</p>}
      {f.tx.length === 0 && <p className="text-sm text-ink-faint">Noch keine Buchungen.</p>}
    </StatKarte>
  );
}
